import { useState, useEffect, useRef } from 'react';
import { useCamera } from '../camera/useCamera';
import { useGetRecentInspections, useGetKPIs } from '../hooks/useQueries';
import {
  Cpu, Wifi, Database, Activity, CheckCircle, XCircle,
  Clock, RefreshCw, Server, Zap,
} from 'lucide-react';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

interface StatusCardProps {
  icon: React.ElementType;
  title: string;
  status: string;
  detail: string;
  active: boolean;
  metric?: string;
  metricLabel?: string;
}

function StatusCard({ icon: Icon, title, status, detail, active, metric, metricLabel }: StatusCardProps) {
  return (
    <div className={`bg-card border rounded-lg p-5 shadow-card-dark transition-colors ${active ? 'border-status-success/30' : 'border-status-danger/30'
      }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded flex items-center justify-center ${active ? 'bg-status-success/10' : 'bg-status-danger/10'
            }`}>
            <Icon className={`w-5 h-5 ${active ? 'text-status-success' : 'text-status-danger'}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground">{detail}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${active ? 'bg-status-success glow-green' : 'bg-status-danger glow-red'}`} />
          <span className={`text-xs font-semibold ${active ? 'text-status-success' : 'text-status-danger'}`}>
            {status}
          </span>
        </div>
      </div>
      {metric && (
        <div className="border-t border-border pt-3 mt-3">
          <p className="text-xs text-muted-foreground">{metricLabel}</p>
          <p className="text-sm font-mono font-semibold text-foreground mt-0.5">{metric}</p>
        </div>
      )}
    </div>
  );
}

export default function SystemStatus() {
  const [uptime, setUptime] = useState(0);
  const startTimeRef = useRef(Date.now());
  const [dbStatus, setDbStatus] = useState<'checking' | 'online' | 'error'>('checking');

  const { isActive: cameraActive, isSupported: cameraSupported } = useCamera({ facingMode: 'user' });
  const { data: recentInspections = [], isLoading: recentLoading, isError: recentError, refetch } = useGetRecentInspections(1);
  const { data: kpis, isLoading: kpisLoading } = useGetKPIs();

  // Uptime counter
  useEffect(() => {
    const interval = setInterval(() => {
      setUptime(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // DB status based on query result
  useEffect(() => {
    if (!recentLoading) {
      setDbStatus(recentError ? 'error' : 'online');
    }
  }, [recentLoading, recentError]);

  const lastInspection = recentInspections[0];
  const cameraConnected = cameraActive || cameraSupported === true;

  const systemComponents = [
    {
      icon: Cpu,
      title: 'AI Model',
      status: 'Active',
      detail: 'DefectNet v2.1 — Bearing & Gear Detection',
      active: true,
      metric: '≥90%',
      metricLabel: 'Detection Accuracy',
    },
    {
      icon: Wifi,
      title: 'Camera System',
      status: cameraConnected ? 'Connected' : 'Disconnected',
      detail: cameraSupported === false ? 'Not supported in this browser' : 'Webcam via MediaDevices API',
      active: cameraConnected,
      metric: cameraActive ? 'Streaming' : 'Standby',
      metricLabel: 'Feed Status',
    },
    {
      icon: Database,
      title: 'Database',
      status: dbStatus === 'checking' ? 'Checking...' : dbStatus === 'online' ? 'Operational' : 'Error',
      detail: 'Local Storage — Inspection Records',
      active: dbStatus === 'online',
      metric: kpisLoading ? '—' : String(kpis?.totalInspections ?? 0),
      metricLabel: 'Total Records',
    },
    {
      icon: Server,
      title: 'Backend Engine',
      status: dbStatus === 'online' ? 'Running' : 'Unreachable',
      detail: 'VisionInspect Local Engine',
      active: dbStatus === 'online',
      metric: dbStatus === 'online' ? 'Healthy' : 'Degraded',
      metricLabel: 'Health',
    },
  ];

  const allHealthy = systemComponents.every(c => c.active);
  const healthyCount = systemComponents.filter(c => c.active).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">System Status</h2>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
            Real-time health monitoring for all system components
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-electric-blue transition-colors px-3 py-1.5 rounded border border-border hover:border-electric-blue/30"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* Overall health banner */}
      <div className={`flex items-center gap-4 p-4 rounded-lg border ${allHealthy
          ? 'bg-status-success/10 border-status-success/30'
          : 'bg-status-warning/10 border-status-warning/30'
        }`}>
        {allHealthy ? (
          <CheckCircle className="w-6 h-6 text-status-success flex-shrink-0" />
        ) : (
          <XCircle className="w-6 h-6 text-status-warning flex-shrink-0" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-bold ${allHealthy ? 'text-status-success' : 'text-status-warning'}`}>
            {allHealthy ? 'All Systems Operational' : `${healthyCount}/${systemComponents.length} Systems Online`}
          </p>
          <p className="text-xs text-muted-foreground">
            {allHealthy
              ? 'VisionInspect is running normally. All components are healthy.'
              : 'Some components require attention. Check individual status below.'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">System Uptime</p>
          <p className="text-sm font-mono font-semibold text-foreground">{formatUptime(uptime)}</p>
        </div>
      </div>

      {/* Component status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {systemComponents.map(comp => (
          <StatusCard key={comp.title} {...comp} />
        ))}
      </div>

      {/* Last inspection & metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Last inspection */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">Last Inspection</h3>
          </div>
          {recentLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-4 bg-muted/50 rounded animate-pulse" />
              ))}
            </div>
          ) : lastInspection ? (
            <div className="space-y-3">
              {[
                { label: 'Component ID', value: lastInspection.componentId, mono: true },
                { label: 'Timestamp', value: formatTimestamp(lastInspection.timestamp) },
                { label: 'Severity Score', value: `${lastInspection.severityScore}/100` },
                { label: 'Model Confidence', value: `${lastInspection.modelConfidence}%` },
              ].map(({ label, value, mono }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <span className={`text-xs font-semibold text-foreground ${mono ? 'font-mono text-electric-blue' : ''}`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No inspections recorded yet</p>
          )}
        </div>

        {/* Performance metrics */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">Performance Metrics</h3>
          </div>
          <div className="space-y-4">
            {[
              {
                label: 'AI Model Accuracy',
                value: kpisLoading ? 0 : Number(kpis?.modelAccuracy ?? 90),
                display: kpisLoading ? '—' : `≥${kpis?.modelAccuracy ?? 90}%`,
                color: 'oklch(0.65 0.18 145)',
              },
              {
                label: 'Processing Speed',
                value: 85,
                display: '< 5s target',
                color: 'oklch(0.72 0.18 220)',
              },
              {
                label: 'System Health',
                value: Math.round((healthyCount / systemComponents.length) * 100),
                display: `${Math.round((healthyCount / systemComponents.length) * 100)}%`,
                color: allHealthy ? 'oklch(0.65 0.18 145)' : 'oklch(0.75 0.18 75)',
              },
            ].map(({ label, value, display, color }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <span className="text-xs font-mono font-semibold text-foreground">{display}</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${value}%`, background: color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System info table */}
      <div className="bg-card border border-border rounded-lg shadow-card-dark">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Zap className="w-4 h-4 text-electric-blue" />
          <h3 className="text-sm font-semibold text-foreground">System Information</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'Application', value: 'VisionInspect v1.0' },
              { label: 'AI Engine', value: 'DefectNet v2.1' },
              { label: 'Platform', value: 'VisionInspect Local Engine' },
              { label: 'Detection Classes', value: 'Scratch, Crack, Warp, Discoloration' },
              { label: 'Target Components', value: 'Bearings & Gears' },
              { label: 'Session Uptime', value: formatUptime(uptime) },
            ].map(({ label, value }) => (
              <div key={label} className="bg-muted/20 rounded p-3">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
                <p className="text-xs font-mono text-foreground">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
